import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import type { UserRole } from "../types/auth.types";
import { userHasRouteAccess } from "../utils/roleUtils";

interface RoleGuardProps {
  children: React.ReactNode;
  /** Exact backend role strings allowed to view this route */
  allowedRoles?: UserRole[];
  /**
   * When set, checks this path against routePermissions.ts instead of allowedRoles.
   */
  permissionPath?: string;
}

/**
 * Authorization guard — blocks authenticated users without the required backend role.
 * Redirects unauthorized users to /unauthorized.
 */
const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles = [],
  permissionPath,
}) => {
  const { role, isAuthReady, isAuthenticated, canAccessRoute } = useAuth();
  const location = useLocation();

  if (!isAuthReady) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const hasAccess = permissionPath
    ? canAccessRoute(permissionPath)
    : userHasRouteAccess(role, allowedRoles);

  if (!hasAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default RoleGuard;
